import { scopedDbName } from '@/services/scopedDB'

// src/utils/indexedDB.js

export default class IndexedDBStore {
  constructor(dbName = 'couplustDB', storeName = 'keywords', version = 5) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.version = version;
  }

  // DB 연결 및 초기화
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(scopedDbName(this.dbName), this.version);

      request.onerror = () => {
        reject(new Error('DB 연결에 실패했습니다.'));
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // 기존 keywords 스토어 생성 (keywordCollect용)
        if (!db.objectStoreNames.contains('keywords')) {
          db.createObjectStore('keywords', { keyPath: 'id', autoIncrement: true });
        }

        // keywordAnalysis 스토어 생성
        if (!db.objectStoreNames.contains('keywordAnalysis')) {
          db.createObjectStore('keywordAnalysis', { keyPath: 'id', autoIncrement: true });
        }

        // categoryAnalysis 스토어 생성
        if (!db.objectStoreNames.contains('categoryAnalysis')) {
          db.createObjectStore('categoryAnalysis', { keyPath: 'id', autoIncrement: true });
        }

        // keywordDataCollect 스토어 생성
        if (!db.objectStoreNames.contains('keywordDataCollect')) {
          db.createObjectStore('keywordDataCollect', { keyPath: 'id', autoIncrement: true });
        }

        // sourcingKeywords 스토어 생성 (AI상품소싱용)
        if (!db.objectStoreNames.contains('sourcingKeywords')) {
          db.createObjectStore('sourcingKeywords', { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  }

  // 키워드 데이터 저장
  async saveKeywords(keywords) {
    if (!this.db) await this.initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // 기존 데이터 삭제
      store.clear();

      // 새 데이터 저장
      const request = store.add({ id: 1, keywords: keywords });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('키워드 저장에 실패했습니다.'));
    });
  }

  // 키워드 데이터 불러오기
  async getKeywords() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(1);

      request.onsuccess = () => {
        resolve(request.result ? request.result.keywords : []);
      };
      request.onerror = () => reject(new Error('키워드 불러오기에 실패했습니다.'));
    });
  }

  // 데이터 삭제
  async clearKeywords() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('키워드 삭제에 실패했습니다.'));
    });
  }

  // 분석 결과 데이터 저장 (keywordAnalysis 전용)
  async saveAnalysisResults(analysisResults) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // 기존 분석 결과 삭제 후 새로 저장
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => {
        const addRequest = store.add({ id: 2, analysisResults: analysisResults });
        addRequest.onsuccess = () => resolve();
        addRequest.onerror = () => reject(new Error('분석 결과 저장에 실패했습니다.'));
      };
      clearRequest.onerror = () => reject(new Error('기존 분석 결과 삭제에 실패했습니다.'));
    });
  }

  // 분석 결과 데이터 불러오기 (keywordAnalysis 전용)
  async getAnalysisResults() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(2);

      request.onsuccess = () => {
        resolve(request.result ? request.result.analysisResults : []);
      };
      request.onerror = () => reject(new Error('분석 결과 불러오기에 실패했습니다.'));
    });
  }

  // 키워드 그룹 저장 (keywordCollect용)
  async saveKeywordGroups(keywordGroups) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // 키워드 그룹 데이터 저장 (id: 3 사용)
      const request = store.put({ id: 3, keywordGroups: keywordGroups });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('키워드 그룹 저장에 실패했습니다.'));
    });
  }

  // 키워드 그룹 불러오기 (keywordCollect용)
  async getKeywordGroups() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(3);

      request.onsuccess = () => {
        resolve(request.result ? request.result.keywordGroups : {});
      };
      request.onerror = () => reject(new Error('키워드 그룹 불러오기에 실패했습니다.'));
    });
  }

  // 특정 키워드 그룹 삭제 (keywordCollect용)
  async deleteKeywordGroup(groupName) {
    if (!this.db) await this.initDB();

    try {
      const keywordGroups = await this.getKeywordGroups();
      delete keywordGroups[groupName];
      await this.saveKeywordGroups(keywordGroups);
    } catch (error) {
      throw new Error('키워드 그룹 삭제에 실패했습니다.');
    }
  }

  // ===== keywordDataCollect 전용 메서드들 =====

  // 키워드 데이터 수집 그룹 저장
  async saveKeywordDataGroups(keywordDataGroups) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readwrite');
      const store = transaction.objectStore('keywordDataCollect');

      // 키워드 데이터 그룹 저장 (id: 1 사용)
      const request = store.put({ id: 1, keywordDataGroups: keywordDataGroups });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('키워드 데이터 그룹 저장에 실패했습니다.'));
    });
  }

  // 키워드 데이터 수집 그룹 불러오기
  async getKeywordDataGroups() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readonly');
      const store = transaction.objectStore('keywordDataCollect');
      const request = store.get(1);

      request.onsuccess = () => {
        resolve(request.result ? request.result.keywordDataGroups : {});
      };
      request.onerror = () => reject(new Error('키워드 데이터 그룹 불러오기에 실패했습니다.'));
    });
  }

  // 특정 키워드 데이터 그룹의 분석 결과 저장
  async saveKeywordDataAnalysis(groupName, analysisData) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readwrite');
      const store = transaction.objectStore('keywordDataCollect');

      // 그룹별 분석 데이터 저장 (id: `analysis_${groupName}` 형태)
      const request = store.put({
        id: `analysis_${groupName}`,
        groupName: groupName,
        analysisData: analysisData,
        savedAt: new Date().toISOString()
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('키워드 데이터 분석 결과 저장에 실패했습니다.'));
    });
  }

  // 키워드 그룹 데이터 저장
  async saveKeywordDataGroup(groupName, groupData) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readwrite');
      const store = transaction.objectStore('keywordDataCollect');

      const request = store.put({
        id: `group_${groupName}`,
        groupName: groupName,
        ...groupData,
        savedAt: new Date().toISOString()
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('키워드 그룹 저장에 실패했습니다.'));
    });
  }

  // 키워드 그룹 데이터 불러오기
  async getKeywordDataGroup(groupName) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readonly');
      const store = transaction.objectStore('keywordDataCollect');
      const request = store.get(`group_${groupName}`);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error('키워드 그룹 불러오기에 실패했습니다.'));
    });
  }

  // 키워드 그룹 삭제
  async deleteKeywordDataGroup(groupName) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readwrite');
      const store = transaction.objectStore('keywordDataCollect');

      // 그룹 데이터와 분석 데이터 모두 삭제
      const deleteGroup = store.delete(`group_${groupName}`);
      const deleteAnalysis = store.delete(`analysis_${groupName}`);

      let completed = 0;
      const checkComplete = () => {
        completed++;
        if (completed === 2) resolve();
      };

      deleteGroup.onsuccess = checkComplete;
      deleteAnalysis.onsuccess = checkComplete;
      deleteGroup.onerror = () => reject(new Error('키워드 그룹 삭제에 실패했습니다.'));
      deleteAnalysis.onerror = checkComplete; // 분석 데이터가 없을 수도 있으므로 에러 무시
    });
  }

  // 특정 키워드 데이터 그룹의 분석 결과 불러오기
  async getKeywordDataAnalysis(groupName) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readonly');
      const store = transaction.objectStore('keywordDataCollect');
      const request = store.get(`analysis_${groupName}`);

      request.onsuccess = () => {
        resolve(request.result ? request.result.analysisData : null);
      };
      request.onerror = () => reject(new Error('키워드 데이터 분석 결과 불러오기에 실패했습니다.'));
    });
  }

  // 모든 키워드 데이터 그룹 목록 가져오기 (메타데이터 포함)
  async getKeywordDataGroupsWithMetadata() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keywordDataCollect'], 'readonly');
      const store = transaction.objectStore('keywordDataCollect');
      const request = store.getAll();

      request.onsuccess = () => {
        const results = request.result;
        const groupsWithMetadata = {};

        results.forEach(item => {
          // item.id가 문자열이고 'analysis_'로 시작하는 경우만 처리
          if (item.id && typeof item.id === 'string' && item.id.startsWith('analysis_')) {
            const groupName = item.groupName;
            if (groupName) {
              groupsWithMetadata[groupName] = {
                groupName: groupName,
                savedAt: item.savedAt || new Date().toISOString(),
                dataSize: item.analysisData ? JSON.stringify(item.analysisData).length : 0,
                productCount: item.analysisData?.analysisResults?.length || 0
              };
            }
          }
        });

        resolve(groupsWithMetadata);
      };
      request.onerror = () => reject(new Error('키워드 데이터 그룹 메타데이터 불러오기에 실패했습니다.'));
    });
  }
}